import { Activity } from "lucide-react";
import { useMarketAwarenessDossier } from "../../hooks/useMarketAwarenessDossier";
import {
  formatMarketPriceActionLine,
  formatMarketPricesOfInterest,
} from "../../lib/market-price-action-reference";
import type { MarketPriceInterestLevel } from "../../lib/market-price-action-reference";
import type { MarketAwarenessDossier } from "../../types/market-awareness";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface MarketPriceActionReferenceProps {
  symbol: string;
  label?: string;
  className?: string;
  variant?: "bulletin" | "form" | "tiltscout";
  initialDossier?: MarketAwarenessDossier | null;
}

export function MarketPriceActionReference({
  symbol,
  label = "Price action",
  className = "",
  variant = "bulletin",
  initialDossier = null,
}: MarketPriceActionReferenceProps) {
  const { dossier, isLoading, error } = useMarketAwarenessDossier(symbol, {
    initialDossier,
  });
  const line = formatMarketPriceActionLine(dossier, symbol);
  const pricesOfInterest = formatMarketPricesOfInterest(dossier, symbol);
  const detail = error
    ? "market awareness unavailable"
    : isLoading && !dossier
      ? "loading market awareness"
      : `${line.move}; ${line.vwap}`;
  const title =
    variant === "form"
      ? `${line.instrument} ${line.price} - ${detail}; ${line.source}; ${line.asOf}`
      : `${pricesOfInterest.instrument} ${pricesOfInterest.price} - ${pricesOfInterest.levels
          .map((level) => `${level.label} ${level.valueText}`)
          .join("; ")}; ${pricesOfInterest.source}; ${pricesOfInterest.asOf}`;

  if (variant === "tiltscout") {
    return (
      <section
        data-market-price-action={variant}
        className={`flex h-full min-h-0 flex-col overflow-hidden ${className}`}
        title={title}
      >
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--fintheon-accent)]/78">
            {label}
          </p>
          <p className="truncate font-mono text-[9px] text-[var(--fintheon-text)]/38">
            {formatSourceStateLabel(pricesOfInterest.sourceState)}
          </p>
        </div>
        <div className="mt-2 min-h-0 flex-1 overflow-hidden">
          {pricesOfInterest.levels.map((level) => (
            <PriceInterestRow key={level.id} level={level} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <div
      data-market-price-action={variant}
      className={`min-w-0 rounded-[4px] bg-[var(--fintheon-bg)]/22 px-2 py-1.5 ${className}`}
      title={title}
    >
      <div className="flex items-center gap-1 text-[9px] text-[var(--fintheon-muted)]">
        <Activity className="h-3 w-3" aria-hidden />
        <span>{label}</span>
        <span className="ml-auto font-mono tracking-[0.06em] text-[var(--fintheon-muted)]/60">
          {formatSourceStateLabel(
            variant === "form"
              ? line.sourceState
              : pricesOfInterest.sourceState,
          )}
        </span>
      </div>
      {variant === "form" ? (
        <p className="mt-0.5 truncate text-[10px] leading-snug text-[var(--fintheon-text)]/78">
          {line.instrument} {line.price} - {detail}
        </p>
      ) : (
        <div className="mt-1.5 grid gap-1">
          {pricesOfInterest.levels.map((level) => (
            <div
              key={level.id}
              className="grid min-w-0 grid-cols-[minmax(46px,112px)_minmax(66px,max-content)_78px] items-baseline gap-2 font-mono text-[10px] leading-none"
            >
              <span className="flex min-w-0 items-center gap-1.5 text-[var(--fintheon-text)]/48">
                <span className="truncate">{level.label}</span>
                <span className="shrink-0 rounded-[3px] bg-[var(--fintheon-accent)]/10 px-1 py-[1px] text-[8px] leading-none text-[var(--fintheon-accent)]/78">
                  {level.sourceLabel}
                </span>
              </span>
              <span
                className="whitespace-nowrap text-right tabular-nums"
                style={{ color: toneColor(level.tone) }}
              >
                <FintheonNumericText text={level.valueText} />
              </span>
              <span
                className="whitespace-nowrap text-right tabular-nums"
                style={{ color: toneColor(level.tone) }}
              >
                {level.distanceText}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PriceInterestRow({ level }: { level: MarketPriceInterestLevel }) {
  const tone = toneClass(level.tone);
  return (
    <div className="relative grid grid-cols-[70px_minmax(80px,1fr)_58px_54px] items-center gap-1 overflow-hidden py-1.5">
      <span className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--fintheon-accent)]/16 to-transparent" />
      <span className="truncate font-mono text-[10.5px] text-[var(--fintheon-text)]/44">
        {level.label}
      </span>
      <span
        className={`whitespace-nowrap text-right font-mono text-sm font-semibold tabular-nums ${tone}`}
      >
        <FintheonNumericText text={level.valueText} />
      </span>
      <span
        className={`col-span-2 justify-self-end whitespace-nowrap text-right font-mono text-[10.5px] tabular-nums ${tone}`}
      >
        {level.distanceText}
      </span>
    </div>
  );
}

function formatSourceStateLabel(value: string) {
  return value
    .split(/([\s/-]+)/)
    .map((part) =>
      /^[A-Za-z]+$/.test(part)
        ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        : part,
    )
    .join("");
}

function toneClass(tone: MarketPriceInterestLevel["tone"]) {
  if (tone === "bullish") return "text-emerald-300/82";
  if (tone === "bearish") return "text-red-300/82";
  return "text-[var(--fintheon-accent)]/78";
}

function toneColor(tone: MarketPriceInterestLevel["tone"]) {
  if (tone === "bullish") return "var(--fintheon-bullish)";
  if (tone === "bearish") return "var(--fintheon-bearish)";
  return "var(--fintheon-primary, var(--fintheon-accent))";
}
